import type { ReportData } from '../../../hooks/useReports';

interface Props {
  data: ReportData;
  loading: boolean;
}

const fmt = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

export const ExecutiveSummary = ({ data, loading }: Props) => {
  const totalOrigin = data.distribConta + data.distribCartao;
  const pctCartao = totalOrigin > 0 ? Math.round((data.distribCartao / totalOrigin) * 100) : 0;

  const cf30 = data.cashFlow30;
  const bp = data.balanceProjection;

  const cards = [
    {
      label: 'Saldo Atual',
      value: fmt(bp.currentBalance),
      sub: 'Soma de todas as contas',
      icon: 'ri-bank-line',
      color: bp.currentBalance >= 0 ? 'emerald' : 'red',
    },
    {
      label: 'Saldo Projetado',
      value: fmt(bp.estimatedEndBalance),
      sub: `Fim do mês estimado`,
      icon: bp.riskLevel === 'danger' ? 'ri-alarm-warning-line' : bp.riskLevel === 'attention' ? 'ri-error-warning-line' : 'ri-shield-check-line',
      color: bp.riskLevel === 'danger' ? 'red' : bp.riskLevel === 'attention' ? 'amber' : 'emerald',
    },
    {
      label: 'A Pagar (30 dias)',
      value: fmt(cf30.expenses),
      sub: `${cf30.items.filter(i => i.type === 'despesa').length} lançamentos`,
      icon: 'ri-arrow-down-circle-line',
      color: 'orange',
    },
    {
      label: 'A Receber (30 dias)',
      value: fmt(cf30.income),
      sub: `${cf30.items.filter(i => i.type === 'receita').length} lançamentos`,
      icon: 'ri-arrow-up-circle-line',
      color: 'emerald',
    },
    {
      label: 'Recorrências Futuras',
      value: fmt(data.cashFlow90.items.filter(i => i.recurrence).reduce((s, i) => s + i.amount, 0)),
      sub: 'Próximos 90 dias',
      icon: 'ri-repeat-2-line',
      color: 'sky',
    },
    {
      label: 'Parcelamentos Futuros',
      value: fmt(data.cashFlow90.items.filter(i => i.installments > 1).reduce((s, i) => s + i.amount, 0)),
      sub: 'Próximos 90 dias',
      icon: 'ri-price-tag-3-line',
      color: 'violet',
    },
    {
      label: 'Total Vencido',
      value: fmt(bp.overdueAmount),
      sub: data.filteredTransactions.filter(t => ['vencido','atrasado'].includes(t.status)).length + ' lançamentos',
      icon: 'ri-time-line',
      color: bp.overdueAmount > 0 ? 'red' : 'gray',
    },
    {
      label: 'Uso do Cartão',
      value: `${pctCartao}%`,
      sub: `${fmt(data.distribCartao)} no período`,
      icon: 'ri-bank-card-line',
      color: pctCartao > 60 ? 'amber' : 'indigo',
    },
  ];

  const colorMap: Record<string, { bg: string; border: string; text: string; ibg: string }> = {
    emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-600', ibg: 'bg-emerald-100' },
    red:     { bg: 'bg-red-50',     border: 'border-red-200',     text: 'text-red-500',     ibg: 'bg-red-100' },
    amber:   { bg: 'bg-amber-50',   border: 'border-amber-200',   text: 'text-amber-600',   ibg: 'bg-amber-100' },
    orange:  { bg: 'bg-orange-50',  border: 'border-orange-200',  text: 'text-orange-500',  ibg: 'bg-orange-100' },
    sky:     { bg: 'bg-sky-50',     border: 'border-sky-200',     text: 'text-sky-600',     ibg: 'bg-sky-100' },
    violet:  { bg: 'bg-violet-50',  border: 'border-violet-200',  text: 'text-violet-600',  ibg: 'bg-violet-100' },
    indigo:  { bg: 'bg-indigo-50',  border: 'border-indigo-200',  text: 'text-indigo-600',  ibg: 'bg-indigo-100' },
    gray:    { bg: 'bg-gray-50',    border: 'border-gray-200',    text: 'text-gray-500',    ibg: 'bg-gray-100' },
  };

  if (loading) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="h-24 bg-gray-100 rounded-2xl animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      {cards.map(card => {
        const c = colorMap[card.color] ?? colorMap.gray;
        return (
          <div key={card.label} className={`rounded-2xl border p-4 ${c.bg} ${c.border}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] text-gray-500 font-500 leading-tight">{card.label}</span>
              <div className={`w-7 h-7 flex items-center justify-center rounded-xl ${c.ibg}`}>
                <i className={`${card.icon} text-[15px] ${c.text}`} />
              </div>
            </div>
            <p className={`font-700 text-[15px] leading-tight ${c.text}`}>{card.value}</p>
            <p className="text-[10px] text-gray-400 mt-0.5 truncate">{card.sub}</p>
          </div>
        );
      })}
    </div>
  );
};
